import { LoadingDark } from '@musinsa/mds';
import { type FC, type HTMLAttributes, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useInfiniteListContext } from './Root';

type InfiniteListTriggerProps = HTMLAttributes<HTMLHRElement>;

export const Trigger: FC<InfiniteListTriggerProps> = ({ className, ...props }) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const ref = useRef<HTMLHRElement>(null);

  const { loadMore, isPending, hasMore } = useInfiniteListContext();

  const observerCallback = useRef<IntersectionObserverCallback>(([entry]) => {
    if (entry.isIntersecting) {
      loadMore();
    }
  });
  useEffect(() => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(observerCallback.current);
    }

    if (ref.current) {
      observerRef.current.observe(ref.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore]);

  if (!hasMore) return null;

  if (isPending)
    return (
      <div className="py-24 w-full flex items-center justify-center" role="status" aria-busy="true">
        <LoadingDark size="size24x24" />
      </div>
    );

  return (
    <hr
      ref={ref}
      role="presentation"
      className={twMerge('pointer-events-none w-1 h-1 invisible', className)}
      {...props}
    />
  );
};

Trigger.displayName = 'InfiniteListTrigger';
